import {
  isBetweenOperator,
  Operators,
  operatorRequiresValue,
} from "@monolith-forensics/monolith-ui";
import type { Operator, Query, Rule } from "@monolith-forensics/monolith-ui";

/**
 * monolith-api's shared QueryFilterSchema (filterResolver.types.ts) validates
 * `filter.rules[].operator.label` against a closed enum, OperatorSchema. The
 * assets and validations query routes apply it, and four mfui labels are
 * spelled differently there ("is", "is not", "between", "not between") —
 * sending them raw is a 400. The API resolver only reads `operator.value`;
 * the label is validation-only, so we rewrite it by value. Keys cover mfui's
 * 14 operators plus the custom startsWith/endsWith pair a filter def may add
 * (both natively supported by the API resolver).
 */
const API_OPERATOR_LABELS: Record<string, string> = {
  "=": "equals",
  "!=": "not equals",
  "<": "less than",
  ">": "greater than",
  "<=": "less than or equal",
  ">=": "greater than or equal",
  contains: "contains",
  doesNotContain: "does not contain",
  startsWith: "starts with",
  endsWith: "ends with",
  isEmpty: "is empty",
  isNotEmpty: "is not empty",
  in: "is any of",
  nin: "is none of",
  between: "is between",
  notBetween: "is not between",
};

/**
 * The only operators that consume the whole selection. Everything else that
 * takes a value takes exactly one (two for the between pair).
 */
const MULTI_VALUE_OPERATORS: ReadonlySet<string> = new Set([
  Operators.In.value,
  Operators.NIn.value,
]);

/**
 * mfui only reshapes a rule's value when the operator being switched *to* is a
 * between operator (`resolveRuleValueForOperator`), so going from "between"
 * back to a single-value operator leaves the second value in place. The API
 * destructures `value` as-is and pushes the whole array into `= (?)`, which
 * mysql expands to `= ('a', 'b')` — MySQL then rejects it with
 * ER_OPERAND_COLUMNS. connection.js resolves that 500 as `{ success: false }`,
 * so the table would render empty with no error at all. Clamping here keeps
 * the array matched to what the operator actually reads.
 */
const clampValueArity = (
  value: Rule["value"],
  operator: Operator
): Rule["value"] => {
  // The API's IS_EMPTY / IS_NOT_EMPTY branches push no args, and RuleSchema
  // makes `value` optional — so a value left over from the previous operator
  // is dropped rather than sent along.
  if (!operatorRequiresValue(operator)) return undefined;
  if (!value) return value;
  if (MULTI_VALUE_OPERATORS.has(operator.value)) return value;
  return value.slice(0, isBetweenOperator(operator) ? 2 : 1);
};

/**
 * Rebuild an mfui filter `Query` as the shape the API's strict
 * QueryFilterSchema accepts, for any table posting to a route validated with
 * it: operator labels normalized to the API enum spellings, values clamped to
 * the operator's arity, and rules reduced to the RuleSchema keys (mfui's
 * UI-only `options` label cache is dropped rather than trusting the nested
 * schema to strip it). Unknown operator values keep their label, which
 * only matters if a filter def ever adds an operator outside the API enum —
 * none do today, and OperatorSchema would 400 the whole request if one did.
 * `undefined` passes through so the body omits `filter`.
 */
export const toApiFilter = (filter: Query | undefined): Query | undefined => {
  if (!filter) return undefined;
  return {
    combinator: filter.combinator,
    rules: filter.rules.map((rule) => ({
      id: rule.id,
      dataField: rule.dataField,
      label: rule.label,
      value: clampValueArity(rule.value, rule.operator),
      operator: {
        value: rule.operator.value,
        label: API_OPERATOR_LABELS[rule.operator.value] ?? rule.operator.label,
      },
    })),
  };
};
